import { ChangeDetectionStrategy, Component, computed, inject, input, output, SecurityContext } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { MediaRecording } from 'src/app/models/media-recording.model';
import { resolveMediaRecordingDiarizationInterventions } from '../media-recording-diarization-page/media-recording-diarization-page.component';
import { MeetingRecordSummaryGenerationStatus } from 'src/app/models/meeting-record-summary-generation-status.type';
import { ApiService } from 'src/app/services/api.service';
import { SelectorDialogComponent, SelectorDialogDataRow } from '../../selector-dialog/selector-dialog.component';

@Component({
  selector: 'app-meeting-record-summary-tab',
  templateUrl: './meeting-record-summary-tab.component.html',
  styleUrls: ['./meeting-record-summary-tab.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
})
export class MeetingRecordSummaryTabComponent {
  ai_summary_html = input<string | null>(null);

  meeting_record_id = input<number | null>(null);

  media_recordings = input<MediaRecording[]>([]);

  can_generate_summary = input(false);

  summary_generation_status = input<MeetingRecordSummaryGenerationStatus | null>(null);

  summary_generation_status_change = output<MeetingRecordSummaryGenerationStatus | null>();

  diarization_seek = output<number>();

  protected has_eligible_diarization = computed(() =>
    this.media_recordings().some(recording => this.isMediaRecordingEligibleForSummary(recording))
  );

  protected has_summary_content = computed(() => this.plainSummaryText(this.ai_summary_html()) !== '');

  protected is_summary_generation_in_flight = computed(() => this.summary_generation_status() === 'processing');

  protected is_generate_summary_disabled = computed(
    () => !this.can_generate_summary() || !this.has_eligible_diarization() || this.is_summary_generation_in_flight()
  );

  protected generate_summary_button_tooltip = computed(() => {
    if (this.is_summary_generation_in_flight()) {
      return 'Ya se está generando el resumen.';
    }

    if (!this.has_eligible_diarization()) {
      return 'Necesitas una grabación transcrita con diarización.';
    }

    return '';
  });

  protected ai_summary_html_safe = computed(() => {
    const ai_summary_html = this.ai_summary_html() ?? '';

    return this.sanitizer.sanitize(SecurityContext.HTML, ai_summary_html) ?? '';
  });

  private api = inject(ApiService);

  private dialog = inject(MatDialog);

  private snack_bar = inject(MatSnackBar);

  private sanitizer = inject(DomSanitizer);

  protected onSummaryReportClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const link = target?.closest?.('[data-diarization-seconds]');

    if (!link) {
      return;
    }

    event.preventDefault();

    const seconds = Number(link.getAttribute('data-diarization-seconds'));

    if (!Number.isNaN(seconds)) {
      this.diarization_seek.emit(seconds);
    }
  }

  protected async onGenerateSummaryClick(): Promise<void> {
    const meeting_record_id = this.meeting_record_id();

    if (meeting_record_id == null || this.is_generate_summary_disabled()) {
      return;
    }

    const eligible_recordings = this.media_recordings().filter(recording => this.isMediaRecordingEligibleForSummary(recording));

    if (eligible_recordings.length === 0) {
      return;
    }

    const selected_media_recording_id = await this.resolveSelectedMediaRecordingId(eligible_recordings);

    if (selected_media_recording_id == null) {
      return;
    }

    const overwrite_existing_summary = await this.confirmOverwriteIfNeeded();

    if (overwrite_existing_summary === null) {
      return;
    }

    this.summary_generation_status_change.emit('processing');

    try {
      await firstValueFrom(
        this.api.generateMeetingRecordSummary(meeting_record_id, {
          media_recording_id: selected_media_recording_id,
          overwrite_existing_summary,
        })
      );
    } catch {
      this.summary_generation_status_change.emit(null);
      this.snack_bar.open('No se pudo generar el resumen. Inténtalo de nuevo.', 'Cerrar', {
        duration: 6000,
      });
    }
  }

  private isMediaRecordingEligibleForSummary(media_recording: MediaRecording): boolean {
    if (media_recording.transcription_status !== 'completed') {
      return false;
    }

    return resolveMediaRecordingDiarizationInterventions(media_recording).some(intervention => intervention.text.trim() !== '');
  }

  private async resolveSelectedMediaRecordingId(eligible_recordings: MediaRecording[]): Promise<number | null> {
    if (eligible_recordings.length === 1) {
      return eligible_recordings[0].id;
    }

    const selector_rows: SelectorDialogDataRow[] = eligible_recordings.map(recording => ({
      value: recording.id,
      textToShow: this.buildMediaRecordingSelectorLabel(recording),
    }));

    const dialog_ref = this.dialog.open(SelectorDialogComponent, {
      data: {
        dialog_title: 'Selecciona la grabación',
        dialog_ok_button: 'Generar resumen',
        dialog_cancel_button: 'Cancelar',
        data: selector_rows,
      },
    });

    const selected_value = await firstValueFrom(dialog_ref.afterClosed());

    return typeof selected_value === 'number' ? selected_value : null;
  }

  private buildMediaRecordingSelectorLabel(media_recording: MediaRecording): string {
    const concept = media_recording.concept?.trim() || `Grabación #${media_recording.id}`;
    const created_at_label = media_recording.created_at ? new Date(media_recording.created_at).toLocaleString('es-ES') : '';

    return created_at_label !== '' ? `${concept} (${created_at_label})` : concept;
  }

  private async confirmOverwriteIfNeeded(): Promise<boolean | null> {
    if (!this.has_summary_content()) {
      return false;
    }

    const dialog_ref = this.dialog.open(SelectorDialogComponent, {
      data: {
        dialog_title: 'Ya existe un resumen',
        dialog_ok_button: 'Sobrescribir',
        dialog_cancel_button: 'Cancelar',
        data: [
          {
            value: true,
            textToShow: 'Reemplazar el resumen actual por uno nuevo generado con IA',
          },
        ],
      },
    });

    const selected_value = await firstValueFrom(dialog_ref.afterClosed());

    if (selected_value === true) {
      return true;
    }

    return null;
  }

  private plainSummaryText(summary_html: string | null | undefined): string {
    return (summary_html ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .trim();
  }
}
